import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Loader2, Pencil, Save, ShieldCheck, Trash2, UserPlus, X } from "lucide-react";
import {
  SuperAdminService,
  TenantAdministrator,
  TenantOverview,
} from "../../services/SuperAdminService";
import {
  TENANT_SLUG_REGEX,
  isReservedTenantSlug,
  normalizeTenantSlug,
} from "../../utils/tenantSlug";
import { formatPhoneNumber } from "../../utils/format";

type SlugStatus = "idle" | "checking" | "available" | "invalid";

interface EditTenantModalProps {
  tenant: TenantOverview;
  onClose: () => void;
  onTenantUpdated: (updatedTenant: TenantOverview, previousSlug: string) => void;
}

const EditTenantModal = ({ tenant, onClose, onTenantUpdated }: EditTenantModalProps) => {
  const [tenantName, setTenantName] = useState(tenant.clientName);
  const [tenantSlug, setTenantSlug] = useState(tenant.clientSlug);
  const [isSlugTouched, setIsSlugTouched] = useState(false);
  const [slugStatus, setSlugStatus] = useState<SlugStatus>("idle");
  const [slugMessage, setSlugMessage] = useState("");
  const [administrators, setAdministrators] = useState<TenantAdministrator[]>([]);
  const [isLoadingAdministrators, setIsLoadingAdministrators] = useState(true);
  const [isSavingTenant, setIsSavingTenant] = useState(false);
  const [isAddingAdministrator, setIsAddingAdministrator] = useState(false);
  const [removingAdministratorId, setRemovingAdministratorId] = useState<string | null>(null);
  const [editingAdministratorId, setEditingAdministratorId] = useState<string | null>(null);
  const [editingAdministratorName, setEditingAdministratorName] = useState("");
  const [editingAdministratorPhone, setEditingAdministratorPhone] = useState("");
  const [administratorForm, setAdministratorForm] = useState({
    userEmail: "",
    userDisplayName: "",
    userPhoneNumber: "",
    password: "",
  });

  const loadAdministrators = async () => {
    try {
      setIsLoadingAdministrators(true);
      const detail = await SuperAdminService.getTenantDetail(tenant.clientId);
      setAdministrators(detail.administrators);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar administradores do tenant.");
    } finally {
      setIsLoadingAdministrators(false);
    }
  };

  useEffect(() => {
    setTenantName(tenant.clientName);
    setTenantSlug(tenant.clientSlug);
    setIsSlugTouched(false);
    setSlugStatus("idle");
    setSlugMessage("");
    setEditingAdministratorId(null);
    setEditingAdministratorName("");
    setEditingAdministratorPhone("");
    setAdministratorForm({
      userEmail: "",
      userDisplayName: "",
      userPhoneNumber: "",
      password: "",
    });
    void loadAdministrators();
  }, [tenant.clientId, tenant.clientName, tenant.clientSlug]);

  useEffect(() => {
    if (isSlugTouched) {
      return;
    }
    setTenantSlug(normalizeTenantSlug(tenantName));
  }, [isSlugTouched, tenantName]);

  const validateSlug = async (): Promise<string | null> => {
    const normalizedSlug = normalizeTenantSlug(tenantSlug || tenantName);
    setTenantSlug(normalizedSlug);

    if (!normalizedSlug) {
      setSlugStatus("invalid");
      setSlugMessage("Slug do tenant é obrigatório.");
      return null;
    }

    if (!TENANT_SLUG_REGEX.test(normalizedSlug)) {
      setSlugStatus("invalid");
      setSlugMessage("Slug inválido: use apenas letras minúsculas, números e hífen.");
      return null;
    }

    if (isReservedTenantSlug(normalizedSlug)) {
      setSlugStatus("invalid");
      setSlugMessage("Slug reservado. Escolha outro.");
      return null;
    }

    if (normalizedSlug === normalizeTenantSlug(tenant.clientSlug)) {
      setSlugStatus("available");
      setSlugMessage("Slug atual do tenant.");
      return normalizedSlug;
    }

    setSlugStatus("checking");
    setSlugMessage("Validando disponibilidade...");
    const isAvailable = await SuperAdminService.isClientSlugAvailable(normalizedSlug);
    if (!isAvailable) {
      setSlugStatus("invalid");
      setSlugMessage("Slug já está em uso. Escolha outro.");
      return null;
    }

    setSlugStatus("available");
    setSlugMessage("Slug disponível.");
    return normalizedSlug;
  };

  const handleSaveTenant = async (event: React.FormEvent) => {
    event.preventDefault();

    const normalizedTenantName = tenantName.trim();
    if (!normalizedTenantName) {
      toast.error("Nome do tenant é obrigatório.");
      return;
    }

    const validatedSlug = await validateSlug();
    if (!validatedSlug) {
      toast.error("Ajuste o slug do tenant para continuar.");
      return;
    }

    setIsSavingTenant(true);
    try {
      const previousSlug = tenant.clientSlug;
      const updatedTenant = await SuperAdminService.updateTenant(tenant.clientId, {
        clientName: normalizedTenantName,
        clientSlug: validatedSlug,
      });

      toast.success("Tenant atualizado com sucesso.");
      onTenantUpdated(updatedTenant, previousSlug);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao atualizar tenant.");
    } finally {
      setIsSavingTenant(false);
    }
  };

  const handleAddAdministrator = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!administratorForm.userEmail.trim()) {
      toast.error("Email do administrador é obrigatório.");
      return;
    }

    setIsAddingAdministrator(true);
    try {
      const result = await SuperAdminService.addTenantAdministrator(tenant.clientId, {
        userEmail: administratorForm.userEmail,
        userDisplayName: administratorForm.userDisplayName,
        userPhoneNumber: administratorForm.userPhoneNumber
          ? formatPhoneNumber(administratorForm.userPhoneNumber)
          : "",
        password: administratorForm.password,
      });

      const successMessageByAction = {
        created: "Administrador criado e vinculado ao tenant.",
        linked: "Administrador existente vinculado ao tenant.",
        promoted: "Usuário já vinculado ao tenant foi promovido para administrador.",
        already_admin: "Este usuário já era administrador deste tenant.",
      } as const;

      toast.success(successMessageByAction[result.action]);
      setAdministratorForm({
        userEmail: "",
        userDisplayName: "",
        userPhoneNumber: "",
        password: "",
      });
      await loadAdministrators();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao adicionar administrador.");
    } finally {
      setIsAddingAdministrator(false);
    }
  };

  const startAdministratorEdition = (administrator: TenantAdministrator) => {
    setEditingAdministratorId(administrator.userId);
    setEditingAdministratorName(administrator.userDisplayName || "");
    setEditingAdministratorPhone(administrator.userPhoneNumber || "");
  };

  const handleSaveAdministrator = async (userId: string) => {
    if (!editingAdministratorName.trim()) {
      toast.error("Nome do administrador é obrigatório.");
      return;
    }

    try {
      const updatedAdministrator = await SuperAdminService.updateTenantAdministrator(
        tenant.clientId,
        userId,
        {
          userDisplayName: editingAdministratorName,
          userPhoneNumber: editingAdministratorPhone
            ? formatPhoneNumber(editingAdministratorPhone)
            : "",
        }
      );

      setAdministrators((current) =>
        current.map((administrator) =>
          administrator.userId === userId ? updatedAdministrator : administrator
        )
      );
      setEditingAdministratorId(null);
      setEditingAdministratorName("");
      setEditingAdministratorPhone("");
      toast.success("Administrador atualizado com sucesso.");
    } catch (error: any) {
      toast.error(error?.message || "Erro ao atualizar administrador.");
    }
  };

  const handleRemoveAdministrator = async (administrator: TenantAdministrator) => {
    const shouldRemove = window.confirm(
      `Remover o acesso de ${administrator.userDisplayName} deste tenant?`
    );
    if (!shouldRemove) {
      return;
    }

    try {
      setRemovingAdministratorId(administrator.userId);
      await SuperAdminService.removeTenantAdministrator(tenant.clientId, administrator.userId);
      toast.success("Administrador removido do tenant.");
      await loadAdministrators();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao remover administrador.");
    } finally {
      setRemovingAdministratorId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-5xl overflow-hidden rounded-2xl bg-white shadow-2xl">
          <div className="flex items-start justify-between border-b border-neutral-200 px-6 py-5">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary-700">
                Gestão de Tenant
              </p>
              <h2 className="mt-1 text-2xl font-bold text-neutral-900">
                Editar {tenant.clientName}
              </h2>
              <p className="mt-2 text-sm text-neutral-600">
                Renomear o slug muda a URL do tenant. Os dados e funções continuam no mesmo{" "}
                <span className="font-medium text-neutral-800">clientId</span>, mas o slug antigo
                deixa de responder.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-2 text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-800"
              aria-label="Fechar edição do tenant"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid gap-0 lg:grid-cols-[1.05fr_1.35fr]">
            <section className="border-b border-neutral-200 p-6 lg:border-b-0 lg:border-r">
              <div className="mb-5 flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-primary-700" />
                <h3 className="text-lg font-semibold text-neutral-900">Dados do Tenant</h3>
              </div>

              <form onSubmit={handleSaveTenant} className="space-y-5">
                <div>
                  <label className="mb-1 block text-sm font-medium text-neutral-700">
                    Nome do tenant
                  </label>
                  <input
                    value={tenantName}
                    onChange={(event) => setTenantName(event.target.value)}
                    className="w-full rounded-md border border-neutral-300 px-3 py-2 focus:border-primary-500 focus:ring-primary-500"
                    placeholder="Ex: Clínica Central"
                    required
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-neutral-700">
                    Slug do tenant
                  </label>
                  <input
                    value={tenantSlug}
                    onChange={(event) => {
                      setIsSlugTouched(true);
                      setSlugStatus("idle");
                      setSlugMessage("");
                      setTenantSlug(normalizeTenantSlug(event.target.value));
                    }}
                    onBlur={() => {
                      void validateSlug();
                    }}
                    className="w-full rounded-md border border-neutral-300 px-3 py-2 focus:border-primary-500 focus:ring-primary-500"
                    placeholder="ex-clinica-central"
                    required
                  />
                  <p className="mt-1 text-xs text-neutral-500">
                    Apenas letras minúsculas, números e hífen.
                  </p>
                  {slugStatus !== "idle" && slugMessage && (
                    <p
                      className={`mt-1 text-xs ${
                        slugStatus === "available"
                          ? "text-emerald-700"
                          : slugStatus === "checking"
                            ? "text-neutral-500"
                            : "text-red-600"
                      }`}
                    >
                      {slugMessage}
                    </p>
                  )}
                </div>

                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <p className="font-medium">Impacto do slug</p>
                  <p className="mt-1">
                    As páginas públicas e privadas do tenant passam a usar o novo slug
                    imediatamente. O slug antigo não mantém redirecionamento.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSavingTenant}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-600 px-4 py-2.5 font-medium text-white transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSavingTenant ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Salvando tenant...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Salvar dados do tenant
                    </>
                  )}
                </button>
              </form>
            </section>

            <section className="p-6">
              <div className="mb-5 flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-primary-700" />
                <h3 className="text-lg font-semibold text-neutral-900">Administradores</h3>
              </div>

              <div className="mb-5 rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-700">
                Nome e telefone são dados globais do usuário e, se ajustados aqui, refletem em
                outros tenants onde ele também existir. Email e senha ficam para a próxima fase.
                Se o email já existir no sistema, o usuário será apenas vinculado ou promovido para
                administrador neste tenant.
              </div>

              <form onSubmit={handleAddAdministrator} className="grid gap-3 rounded-xl border border-neutral-200 p-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-neutral-700">Email</label>
                  <input
                    type="email"
                    value={administratorForm.userEmail}
                    onChange={(event) =>
                      setAdministratorForm((current) => ({
                        ...current,
                        userEmail: event.target.value,
                      }))
                    }
                    className="w-full rounded-md border border-neutral-300 px-3 py-2 focus:border-primary-500 focus:ring-primary-500"
                    placeholder="admin@empresa.com"
                    required
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-neutral-700">Nome</label>
                  <input
                    value={administratorForm.userDisplayName}
                    onChange={(event) =>
                      setAdministratorForm((current) => ({
                        ...current,
                        userDisplayName: event.target.value,
                      }))
                    }
                    className="w-full rounded-md border border-neutral-300 px-3 py-2 focus:border-primary-500 focus:ring-primary-500"
                    placeholder="Obrigatório só para novo usuário"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-neutral-700">Telefone</label>
                  <input
                    value={administratorForm.userPhoneNumber}
                    onChange={(event) =>
                      setAdministratorForm((current) => ({
                        ...current,
                        userPhoneNumber: event.target.value,
                      }))
                    }
                    className="w-full rounded-md border border-neutral-300 px-3 py-2 focus:border-primary-500 focus:ring-primary-500"
                    placeholder="Opcional"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-neutral-700">Senha</label>
                  <input
                    type="password"
                    value={administratorForm.password}
                    onChange={(event) =>
                      setAdministratorForm((current) => ({
                        ...current,
                        password: event.target.value,
                      }))
                    }
                    className="w-full rounded-md border border-neutral-300 px-3 py-2 focus:border-primary-500 focus:ring-primary-500"
                    placeholder="Obrigatória só para novo usuário"
                  />
                </div>

                <div className="md:col-span-2">
                  <button
                    type="submit"
                    disabled={isAddingAdministrator}
                    className="inline-flex items-center gap-2 rounded-md bg-neutral-900 px-4 py-2.5 font-medium text-white transition hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isAddingAdministrator ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Adicionando administrador...
                      </>
                    ) : (
                      <>
                        <UserPlus className="h-4 w-4" />
                        Adicionar administrador
                      </>
                    )}
                  </button>
                </div>
              </form>

              <div className="mt-6 space-y-3">
                {isLoadingAdministrators ? (
                  <div className="flex items-center justify-center py-10">
                    <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
                  </div>
                ) : administrators.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-neutral-300 p-5 text-sm text-neutral-600">
                    Nenhum administrador encontrado neste tenant.
                  </div>
                ) : (
                  administrators.map((administrator) => {
                    const isEditing = editingAdministratorId === administrator.userId;
                    const isRemoving = removingAdministratorId === administrator.userId;
                    const isLastAdministrator = administrators.length === 1;

                    return (
                      <div
                        key={administrator.userId}
                        className="rounded-xl border border-neutral-200 p-4"
                      >
                        {isEditing ? (
                          <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
                            <input
                              value={editingAdministratorName}
                              onChange={(event) => setEditingAdministratorName(event.target.value)}
                              className="rounded-md border border-neutral-300 px-3 py-2 focus:border-primary-500 focus:ring-primary-500"
                              placeholder="Nome"
                            />
                            <input
                              value={editingAdministratorPhone}
                              onChange={(event) => setEditingAdministratorPhone(event.target.value)}
                              className="rounded-md border border-neutral-300 px-3 py-2 focus:border-primary-500 focus:ring-primary-500"
                              placeholder="Telefone"
                            />
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => void handleSaveAdministrator(administrator.userId)}
                                className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-primary-700"
                              >
                                <Save className="h-4 w-4" />
                                Salvar
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingAdministratorId(null);
                                  setEditingAdministratorName("");
                                  setEditingAdministratorPhone("");
                                }}
                                className="rounded-md border border-neutral-300 px-3 py-2 text-sm font-medium text-neutral-700 transition hover:bg-neutral-50"
                              >
                                Cancelar
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="font-medium text-neutral-900">
                                  {administrator.userDisplayName}
                                </p>
                                {administrator.current_client_id === tenant.clientId && (
                                  <span className="rounded-full bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700">
                                    tenant atual
                                  </span>
                                )}
                              </div>
                              <p className="mt-1 text-sm text-neutral-600">
                                {administrator.userEmail}
                              </p>
                              <p className="mt-1 text-sm text-neutral-500">
                                {administrator.userPhoneNumber || "Telefone não informado"}
                              </p>
                            </div>

                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => startAdministratorEdition(administrator)}
                                className="inline-flex items-center gap-2 rounded-md border border-neutral-300 px-3 py-2 text-sm font-medium text-neutral-700 transition hover:bg-neutral-50"
                              >
                                <Pencil className="h-4 w-4" />
                                Editar
                              </button>
                              <button
                                type="button"
                                onClick={() => void handleRemoveAdministrator(administrator)}
                                disabled={isRemoving || isLastAdministrator}
                                className="inline-flex items-center gap-2 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                                title={
                                  isLastAdministrator
                                    ? "O tenant precisa manter ao menos um administrador."
                                    : "Remover administrador do tenant"
                                }
                              >
                                {isRemoving ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                                Remover
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditTenantModal;
